import { useState, useMemo, useEffect } from 'react';
import { vehicleApi } from '../../lib/api';
import toast from 'react-hot-toast';

// --- Calendar utils ---
const DAYS_OF_WEEK = ['일', '월', '화', '수', '목', '금', '토'];
const MONTHS = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

interface DayCell {
  date: Date;
  key: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  status: 'free' | 'reserved' | 'blocked';
  reservationId?: string;
}

export default function HostCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockedDates, setBlockedDates] = useState<Record<string, 'reserved' | 'blocked'>>({});
  const [loading, setLoading] = useState(true);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Load host vehicles
  useEffect(() => {
    vehicleApi.hostVehicles()
      .then((res: any) => {
        setVehicles(res.vehicles || []);
        if (res.vehicles?.length > 0) setSelectedVehicle(res.vehicles[0].id);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Load availability when vehicle/month changes
  useEffect(() => {
    if (!selectedVehicle) return;
    vehicleApi.getAvailability(selectedVehicle, year, month + 1)
      .then((res: any) => {
        const map: Record<string, 'reserved' | 'blocked'> = {};
        (res.availability || []).forEach((a: any) => {
          map[a.block_date] = a.block_type;
        });
        setBlockedDates(map);
      })
      .catch(() => {});
  }, [selectedVehicle, year, month]);

  const days = useMemo((): DayCell[] => {
    const totalDays = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

    const cells: DayCell[] = [];

    const prevMonthDays = getDaysInMonth(year, month - 1 < 0 ? 11 : month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const date = new Date(year, month - 1, d);
      cells.push({ date, key: `prev-${d}`, isCurrentMonth: false, isToday: false, status: 'free' });
    }

    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d);
      const key = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const status = blockedDates[key] || 'free';
      cells.push({ date, key, isCurrentMonth: true, isToday: key === todayKey, status });
    }

    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const date = new Date(year, month + 1, d);
      cells.push({ date, key: `next-${d}`, isCurrentMonth: false, isToday: false, status: 'free' });
    }

    return cells;
  }, [year, month, blockedDates]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const handleDateClick = async (cell: DayCell) => {
    if (!cell.isCurrentMonth) return;
    setSelectedDate(cell.key);
    if (cell.status === 'blocked') {
      // Unblock
      if (!confirm(`${cell.key} 차단을 해제하시겠습니까?`)) return;
      try {
        const token = localStorage.getItem('carp2p_token');
        await fetch(`/api/vehicles/${selectedVehicle}/availability/${cell.key}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        const res = await vehicleApi.getAvailability(selectedVehicle, year, month + 1);
        const map: Record<string, 'reserved' | 'blocked'> = {};
        (res.availability || []).forEach((a: any) => { map[a.block_date] = a.block_type; });
        setBlockedDates(map);
      } catch (err: any) { /* ignore */ }
    } else if (cell.status === 'free') {
      setShowBlockModal(true);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">캘린더 관리</h1>
          <p className="text-sm text-gray-500 mt-1">차량별 예약 일정 및 차단 일정을 관리하세요</p>
        </div>

        {/* Vehicle selector */}
        <select
          value={selectedVehicle}
          onChange={e => setSelectedVehicle(e.target.value)}
          className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          {vehicles.map((v: any) => (
            <option key={v.id} value={v.id}>{v.make} {v.model}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Month header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
              <button
                onClick={prevMonth}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-slate-900">
                {year}년 {MONTHS[month]}
              </h2>
              <button
                onClick={nextMonth}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 text-center py-3 border-b border-gray-50">
              {DAYS_OF_WEEK.map((day, i) => (
                <div
                  key={day}
                  className={`text-xs font-semibold uppercase tracking-wider ${
                    i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Date grid */}
            <div className="grid grid-cols-7 p-2">
              {days.map((cell) => (
                <button
                  key={cell.key}
                  onClick={() => handleDateClick(cell)}
                  disabled={!cell.isCurrentMonth}
                  className={`
                    relative aspect-square flex items-center justify-center text-sm rounded-xl m-0.5
                    transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-brand-500
                    ${!cell.isCurrentMonth ? 'text-gray-200 cursor-default' : ''}
                    ${cell.isCurrentMonth && cell.status === 'free' && !cell.isToday
                      ? 'text-gray-700 hover:bg-gray-100 hover:text-slate-900'
                      : ''}
                    ${cell.isToday ? 'font-bold text-slate-900 ring-2 ring-slate-900 ring-inset' : ''}
                    ${cell.status === 'reserved' ? 'bg-brand-50 text-brand-700 font-medium' : ''}
                    ${cell.status === 'blocked' ? 'bg-red-50 text-red-600 font-medium line-through' : ''}
                    ${selectedDate === cell.key ? 'ring-2 ring-slate-400' : ''}
                  `}
                >
                  <span>{cell.date.getDate()}</span>
                  {/* Dot indicator for events */}
                  {cell.status !== 'free' && cell.isCurrentMonth && (
                    <span className={`absolute bottom-1.5 w-1 h-1 rounded-full ${
                      cell.status === 'reserved' ? 'bg-brand-500' : 'bg-red-400'
                    }`} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar — Legend + Quick Actions */}
        <div className="space-y-4">
          {/* Legend */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-semibold text-sm text-slate-900 mb-3">범례</h3>
            <div className="space-y-2.5">
              <div className="flex items-center gap-3 text-sm">
                <span className="w-4 h-4 rounded-lg bg-white border border-gray-200" />
                <span className="text-gray-500">예약 가능</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="w-4 h-4 rounded-lg bg-brand-50 border border-brand-200" />
                <span className="text-gray-500">예약 확정</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="w-4 h-4 rounded-lg bg-red-50 border border-red-200" />
                <span className="text-gray-500">차단 일정</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-semibold text-sm text-slate-900 mb-3">일정 관리</h3>
            <div className="space-y-2">
              <button
                onClick={() => selectedDate && setShowBlockModal(true)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <span className="block font-medium text-red-500">+ 일정 차단</span>
                <span className="text-xs text-gray-400">선택한 날짜를 차단합니다</span>
              </button>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
                <span className="block font-medium text-slate-700">예약 상세 보기</span>
                <span className="text-xs text-gray-400">선택한 날짜의 예약 정보</span>
              </button>
            </div>
          </div>

          {/* Upcoming Reservations */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-semibold text-sm text-slate-900 mb-3">다가오는 예약</h3>
            <div className="space-y-2">
              {[
                { date: `${month+1}월 6일~8일`, guest: 'A제작사', status: 'reserved' },
                { date: `${month+1}월 15일~16일`, guest: 'B프로덕션', status: 'reserved' },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 text-sm">
                  <div>
                    <p className="font-medium text-slate-700">{r.guest}</p>
                    <p className="text-xs text-gray-400">{r.date}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700">
                    확정
                  </span>
                </div>
              ))}
              <p className="text-xs text-gray-400 text-center pt-1">API 연동 시 실제 데이터 표시</p>
            </div>
          </div>
        </div>
      </div>

      {/* Block Modal */}
      {showBlockModal && selectedDate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowBlockModal(false)}>
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">일정 차단</h3>
            <p className="text-sm text-gray-500 mb-4">{selectedDate} 일자를 차단하시겠습니까?</p>
            <div className="space-y-3">
              <select className="input-field text-sm">
                <option value="host_blocked">개인 일정</option>
                <option value="maintenance">차량 정비</option>
              </select>
              <div className="flex gap-2">
                <button onClick={() => setShowBlockModal(false)} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
                  취소
                </button>
                <button onClick={async () => {
                  if (!selectedVehicle || !selectedDate) return;
                  try {
                    const token = localStorage.getItem('carp2p_token');
                    await fetch(`/api/vehicles/${selectedVehicle}/availability`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ date: selectedDate, block_type: 'host_blocked' }),
                    });
                    setShowBlockModal(false);
                    // Refresh availability
                    const res = await vehicleApi.getAvailability(selectedVehicle, year, month + 1);
                    const map: Record<string, 'reserved' | 'blocked'> = {};
                    (res.availability || []).forEach((a: any) => { map[a.block_date] = a.block_type; });
                    setBlockedDates(map);
                  } catch (err: any) { /* ignore */ }
                }} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
                  차단하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
